import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { TooltipProvider } from '@monksflow/monks-ui';
import {
  EmailTemplatesErrorState,
  TemplateNameSheet,
  useEmailTemplateMutations,
  useEmailTemplates,
} from '@/features/templates-config';
import { PageTransition } from '@/shared/ui/animations';
import { BreadcrumbPageHeader, ListPageBody, ListPageLayout } from '@/shared/ui/layout';
import { AboutPageDialog } from '@/shared/ui';
import { ROUTES } from '@/shared/config/routes';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useDebounce } from '@/shared/hooks';
import type { SortDirection } from '@/shared/ui/data-table';
import { useTranslation } from 'react-i18next';
import {
  EmailTemplatesEmptyState,
  EmailTemplatesNoResultsState,
} from './components/EmailTemplatesEmptyState';
import { EmailTemplatesTable } from './components/EmailTemplatesTable';
import { EmailTemplatesToolbar } from './components/EmailTemplatesToolbar';
import { compareTemplates, type EmailTemplateSortColumn } from './sortTemplates';

export function EmailTemplatesList() {
  const { t } = useTranslation('knowledge');
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<EmailTemplateSortColumn>('templateName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [createOpen, setCreateOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

  const { templates, isLoading, isError, refetch } = useEmailTemplates();
  const { createTemplate, renameTemplate, deleteTemplate } = useEmailTemplateMutations();

  const visibleTemplates = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    const matching = term
      ? templates.filter((template) => template.templateName.toLowerCase().includes(term))
      : templates;
    return [...matching].sort(compareTemplates(sortBy, sortDirection));
  }, [templates, debouncedSearch, sortBy, sortDirection]);

  const handleSort = useCallback(
    (column: EmailTemplateSortColumn) => {
      // Re-clicking the active column flips it; moving to a new column restarts at ascending,
      // which reads as "sort by this" rather than inheriting the previous column's direction.
      if (column === sortBy) {
        setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
        return;
      }
      setSortBy(column);
      setSortDirection('asc');
    },
    [sortBy],
  );

  const handleCreate = useCallback(
    (templateName: string) => {
      setCreateOpen(false);
      createTemplate.mutate(templateName, {
        // Navigate on the resolved slug rather than re-deriving it here, so the route can never
        // disagree with what was persisted.
        onSuccess: (slug) => void navigate(ROUTES.KNOWLEDGE_HUB_TEMPLATE_DETAIL(slug)),
      });
    },
    [createTemplate, navigate],
  );

  const handleRename = useCallback(
    (slug: string, templateName: string) => renameTemplate.mutate({ slug, templateName }),
    [renameTemplate],
  );

  const handleDelete = useCallback((slug: string) => deleteTemplate.mutate(slug), [deleteTemplate]);

  const searchActive = debouncedSearch.trim().length > 0;
  // Only a cold-start failure reaches this — a failed background refetch keeps the previous rows
  // on screen, which is a better outcome than replacing them with an error.
  const showError = isError && templates.length === 0;
  const showInitialEmpty = !isLoading && !isError && templates.length === 0;
  const showNoResults =
    !isLoading && !isError && templates.length > 0 && visibleTemplates.length === 0 && searchActive;
  // With nothing to search there is nothing for the toolbar to do; mirrors the other list pages.
  const showToolbar = !showInitialEmpty && !showError;

  return (
    <TooltipProvider>
      <ListPageLayout>
        <PageTransition variant="slideUp" delay={0.0}>
          {
            <BreadcrumbPageHeader
              trail={[
                {
                  label: t('knowledgeHub.emailTemplatesHeader.knowledge'),
                  href: ROUTES.KNOWLEDGE_HUB,
                },
              ]}
              page={t('knowledgeHub.emailTemplatesList.emailTemplates')}
              info={{ onClick: () => setIsAboutOpen(true) }}
            />
          }
        </PageTransition>

        <ListPageBody>
          <PageTransition variant="slideUp" delay={0.1} className="flex min-h-0 flex-1 flex-col">
            {showToolbar && (
              <EmailTemplatesToolbar
                search={search}
                onSearchChange={setSearch}
                onCreateClick={() => setCreateOpen(true)}
              />
            )}
            {showError ? (
              <EmailTemplatesErrorState onRetry={() => void refetch()} />
            ) : showInitialEmpty ? (
              <EmailTemplatesEmptyState onCreate={() => setCreateOpen(true)} />
            ) : showNoResults ? (
              <EmailTemplatesNoResultsState onClear={() => setSearch('')} />
            ) : (
              <div className="flex min-h-0 flex-1 flex-col overflow-auto px-section pb-section">
                <EmailTemplatesTable
                  templates={visibleTemplates}
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  isLoading={isLoading}
                  onRename={handleRename}
                  onDelete={handleDelete}
                />
              </div>
            )}
          </PageTransition>
        </ListPageBody>
      </ListPageLayout>

      {/* Unmount on close so the field resets. */}
      {createOpen && (
        <TemplateNameSheet
          mode="create"
          existingSlugs={templates.map((template) => template.slug)}
          onCancel={() => setCreateOpen(false)}
          onSubmit={handleCreate}
        />
      )}

      <AboutPageDialog
        open={isAboutOpen}
        onOpenChange={setIsAboutOpen}
        title={t('knowledgeHub.emailTemplatesList.emailTemplates')}
        description={t('knowledgeHub.emailTemplatesList.description')}
        rows={[]}
      />
    </TooltipProvider>
  );
}
